import { CURRENCY } from './constants'

export function formatNum(n) {
  if (n >= 10000000) return (n / 10000000).toFixed(2) + ' Cr'
  if (n >= 100000) return (n / 100000).toFixed(2) + ' L'
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K'
  return Math.round(n).toLocaleString()
}

export function fmt(n, market = 'India') {
  let m = CURRENCY[market]
  if (!m && market) {
    const key = Object.keys(CURRENCY).find(
      (k) =>
        market.toLowerCase().includes(k.toLowerCase()) ||
        k.toLowerCase().includes(market.toLowerCase()),
    )
    m = key ? CURRENCY[key] : null
  }
  m = m || CURRENCY.India
  return m.format(n, formatNum)
}

export function parseBudget(str) {
  if (!str) return 0
  str = str.replace(/,/g, '').trim()
  if (/cr/i.test(str)) return parseFloat(str) * 10000000
  if (/l(?:akh)?/i.test(str)) return parseFloat(str) * 100000
  if (/k/i.test(str)) return parseFloat(str) * 1000
  return parseFloat(str) || 0
}

export function calculateBudget(inputs) {
  const total = inputs.totalBudget
  const prodFormat = inputs.format
  const shootDays = inputs.shootDays || 20
  const castLevel = inputs.castLevel
  const postLevel = inputs.postLevel
  const vfxShots = inputs.vfxShots || 0
  const stunts = inputs.stunts
  const period = inputs.period
  const music = inputs.music
  const nightShoots = inputs.nightShoots || 0
  const locations = inputs.locations || 5
  const locationType = inputs.locationType
  const market = inputs.market || 'India'

  let atlPct, btlPct, postPct, otherPct

  const presets = {
    'Short Film': { atl: 0.15, btl: 0.55, post: 0.2, other: 0.1 },
    'Feature Film': { atl: 0.3, btl: 0.42, post: 0.18, other: 0.1 },
    'Web Series': { atl: 0.25, btl: 0.45, post: 0.2, other: 0.1 },
    'OTT Original': { atl: 0.28, btl: 0.42, post: 0.2, other: 0.1 },
    Documentary: { atl: 0.1, btl: 0.5, post: 0.28, other: 0.12 },
    'Ad Film / TVC': { atl: 0.2, btl: 0.5, post: 0.2, other: 0.1 },
    'Music Video': { atl: 0.2, btl: 0.55, post: 0.18, other: 0.07 },
    'Stage Play': { atl: 0.35, btl: 0.5, post: 0.05, other: 0.1 },
    'TV Show / Soap': { atl: 0.25, btl: 0.45, post: 0.2, other: 0.1 },
    'Reality Show': { atl: 0.15, btl: 0.55, post: 0.18, other: 0.12 },
    'Corporate Film': { atl: 0.15, btl: 0.55, post: 0.2, other: 0.1 },
    Animation: { atl: 0.2, btl: 0.25, post: 0.45, other: 0.1 },
    'Talk Show': { atl: 0.3, btl: 0.48, post: 0.12, other: 0.1 },
    'Live Event / Concert': { atl: 0.3, btl: 0.55, post: 0.05, other: 0.1 },
    'Podcast / Audio': { atl: 0.2, btl: 0.4, post: 0.3, other: 0.1 },
  }
  const defaultPreset = { atl: 0.28, btl: 0.44, post: 0.18, other: 0.1 }

  const base = presets[prodFormat] || defaultPreset
  atlPct = base.atl
  btlPct = base.btl
  postPct = base.post
  otherPct = base.other

  if (castLevel === 'stars') atlPct = Math.min(atlPct + 0.15, 0.55)
  else if (castLevel === 'established') atlPct = Math.min(atlPct + 0.07, 0.45)
  else if (castLevel === 'emerging') atlPct = Math.max(atlPct - 0.05, 0.08)

  if (vfxShots > 100) {
    postPct += 0.1
    btlPct -= 0.05
  } else if (vfxShots > 30) {
    postPct += 0.05
  }

  if (period === 'full') btlPct += 0.05
  if (stunts === 'major') btlPct += 0.05

  if (postLevel === 'premium') postPct = Math.min(postPct + 0.08, 0.35)
  else if (postLevel === 'basic') postPct = Math.max(postPct - 0.04, 0.08)

  const sum = atlPct + btlPct + postPct + otherPct
  atlPct /= sum
  btlPct /= sum
  postPct /= sum
  otherPct /= sum

  const atlTotal = total * atlPct
  const btlTotal = total * btlPct
  const postTotal = total * postPct
  const otherTotal = total * otherPct

  const scriptAmt = atlTotal * 0.06
  const producerAmt = atlTotal * 0.12
  const directorPct = castLevel === 'stars' ? 0.18 : 0.22
  const directorAmt = atlTotal * directorPct
  const castAmt = atlTotal - scriptAmt - producerAmt - directorAmt
  const castPerActor =
    inputs.castCount > 0 ? castAmt / inputs.castCount : castAmt

  const dailyCrew = shootDays > 0 ? (btlTotal * 0.3) / shootDays : 0
  const nightPremium = nightShoots * dailyCrew * 0.25

  const crewAmt = btlTotal * 0.3 + nightPremium
  const equipAmt = btlTotal * 0.18
  const locationAmt = btlTotal * 0.16
  const artDeptAmt = period === 'full' ? btlTotal * 0.14 : btlTotal * 0.09
  const wardrobeAmt = period === 'full' ? btlTotal * 0.08 : btlTotal * 0.05
  const cateringAmt = btlTotal * 0.04
  const transportAmt = btlTotal * 0.05
  const stuntAmt =
    stunts === 'major'
      ? btlTotal * 0.08
      : stunts === 'minor'
        ? btlTotal * 0.03
        : 0
  const btlMisc =
    btlTotal -
    crewAmt -
    equipAmt -
    locationAmt -
    artDeptAmt -
    wardrobeAmt -
    cateringAmt -
    transportAmt -
    stuntAmt

  const editingAmt = postTotal * 0.22
  const colorAmt = postTotal * 0.12
  const soundAmt = postTotal * 0.18
  const vfxAmt =
    vfxShots > 0
      ? postTotal * (0.15 + Math.min(vfxShots / 500, 0.2))
      : postTotal * 0.05
  const musicAmt =
    music === 'full'
      ? postTotal * 0.2
      : music === 'original'
        ? postTotal * 0.14
        : postTotal * 0.06
  const deliveryAmt = postTotal * 0.06
  const postMisc =
    postTotal -
    editingAmt -
    colorAmt -
    soundAmt -
    vfxAmt -
    musicAmt -
    deliveryAmt

  const contingency = otherTotal * 0.45
  const insurance = otherTotal * 0.25
  const legal = otherTotal * 0.15
  const bond =
    inputs.distrib === 'theatrical' && total > 5000000 ? otherTotal * 0.1 : 0
  const otherMisc = otherTotal - contingency - insurance - legal - bond

  const atlWarning = atlPct > 0.5
  const contingencyOk = contingency >= total * 0.09
  const btlWarning = btlPct < 0.25

  return {
    total,
    atlPct,
    btlPct,
    postPct,
    otherPct,
    atlTotal,
    btlTotal,
    postTotal,
    otherTotal,
    atl: {
      total: atlTotal,
      items: [
        {
          name: 'Story, Script & Rights',
          detail: 'Option fees, screenplay purchase, rewrites',
          amount: scriptAmt,
          flag: 'atl',
        },
        {
          name: 'Producer Fees',
          detail: 'EP, line producer, co-producer compensation',
          amount: producerAmt,
          flag: 'atl',
        },
        {
          name: 'Director Fee',
          detail: 'Prep, shoot, and post-supervision',
          amount: directorAmt,
          flag: 'atl',
        },
        {
          name: 'Principal Cast',
          detail: `${inputs.castCount} cast members — ${fmt(castPerActor, market)} avg per actor`,
          amount: castAmt,
          flag: 'atl',
        },
      ],
    },
    btl: {
      total: btlTotal,
      items: [
        {
          name: 'Production Crew',
          detail: `${shootDays} days · ${nightShoots > 0 ? nightShoots + ' night shoots included' : 'Day shoots'}`,
          amount: crewAmt,
          flag: 'btl',
        },
        {
          name: 'Camera & Equipment',
          detail: 'Camera package, lighting, grip, sound package',
          amount: equipAmt,
          flag: 'btl',
        },
        {
          name: 'Locations & Permits',
          detail: `${locations} locations · ${locationType === 'studio' ? 'Primarily studio/stage' : 'Practical locations'}`,
          amount: locationAmt,
          flag: 'btl',
        },
        {
          name: 'Art Department & Sets',
          detail:
            period === 'full'
              ? 'Full period / costume-heavy production'
              : 'Production design, props, set dressing',
          amount: artDeptAmt,
          flag: 'btl',
        },
        {
          name: 'Wardrobe & Costumes',
          detail:
            period === 'full'
              ? 'Period costumes, fittings, rentals'
              : 'Contemporary wardrobe',
          amount: wardrobeAmt,
          flag: 'btl',
        },
        {
          name: 'Transport & Logistics',
          detail: 'Vehicles, fuel, base camps',
          amount: transportAmt,
          flag: 'btl',
        },
        {
          name: 'Catering & Craft Services',
          detail: `Crew meals × ${shootDays} days`,
          amount: cateringAmt,
          flag: 'btl',
        },
        ...(stunts !== 'none'
          ? [
              {
                name: 'Stunts & Safety',
                detail:
                  stunts === 'major'
                    ? 'Major action sequences, stunt coordinator'
                    : 'Minor stunt work',
                amount: stuntAmt,
                flag: 'btl',
              },
            ]
          : []),
        {
          name: 'Miscellaneous / Contingency (BTL)',
          detail: 'Expendables, overages, production supplies',
          amount: btlMisc,
          flag: 'btl',
        },
      ],
    },
    post: {
      total: postTotal,
      items: [
        {
          name: 'Picture Edit',
          detail: 'Editor fee, editorial room, assembly to locked picture',
          amount: editingAmt,
        },
        {
          name: 'Color Grading',
          detail: `DI color grade — ${postLevel === 'premium' ? 'Premium grade, HDR deliverable' : 'Standard grade'}`,
          amount: colorAmt,
        },
        {
          name: 'Sound Design & Mix',
          detail: 'Sound design, Foley, ADR, final mix',
          amount: soundAmt,
        },
        {
          name: 'Visual Effects (VFX)',
          detail:
            vfxShots > 0
              ? `${vfxShots} VFX shots estimated`
              : 'Basic compositing and cleanup',
          amount: vfxAmt,
        },
        {
          name: 'Music',
          detail:
            music === 'full'
              ? 'Original score + songs, recording, mixing'
              : music === 'original'
                ? 'Original score composition and recording'
                : 'Licensed music + sync clearances',
          amount: musicAmt,
        },
        {
          name: 'Delivery & Distribution Masters',
          detail: 'DCP, digital masters, closed captions, M&E',
          amount: deliveryAmt,
        },
        ...(postMisc > 0
          ? [
              {
                name: 'Post Miscellaneous',
                detail: 'Screenings, QC, additional passes',
                amount: postMisc,
              },
            ]
          : []),
      ],
    },
    other: {
      total: otherTotal,
      items: [
        {
          name: 'Contingency Reserve',
          detail: '10% buffer — held against genuine overages only',
          amount: contingency,
        },
        {
          name: 'Production Insurance',
          detail: 'Cast insurance, equipment, E&O coverage',
          amount: insurance,
        },
        {
          name: 'Legal & Business Affairs',
          detail: 'Entertainment attorney, contracts, clearances',
          amount: legal,
        },
        ...(bond > 0
          ? [
              {
                name: 'Completion Bond',
                detail: 'Required for theatrical / institutional financing',
                amount: bond,
              },
            ]
          : []),
        ...(otherMisc > 0
          ? [
              {
                name: 'Overhead & Administrative',
                detail: 'Office, accounting, payroll service',
                amount: otherMisc,
              },
            ]
          : []),
      ],
    },
    health: {
      atlWarning,
      contingencyOk,
      btlWarning,
      dailyRate: dailyCrew,
      contingencyPct: (contingency / total) * 100,
    },
    intel: {
      atl2btl: (atlTotal / btlTotal).toFixed(2),
      perShootDay: fmt(btlTotal / shootDays, market),
      notes: inputs.notes,
    },
  }
}

export function buildInvestorStatement(budget, inputs) {
  const atl = Math.round(budget.atlPct * 100)
  const btl = Math.round(budget.btlPct * 100)
  const post = Math.round(budget.postPct * 100)
  let stmtText = `This ${inputs.format.toLowerCase()} budget allocates ${atl}% Above the Line and ${btl}% Below the Line — `
  if (atl <= 35)
    stmtText +=
      'indicating a production-forward approach where the majority of spend supports physical execution. '
  else if (atl > 50)
    stmtText +=
      'reflecting a star-driven package where creative talent represents the primary asset for financing and distribution. '
  else
    stmtText +=
      'reflecting a balanced creative-to-execution ratio consistent with mid-range independent production. '
  stmtText += `Post-production at ${post}% `
  stmtText +=
    post >= 20
      ? 'signals a technically ambitious deliverable. '
      : 'is conservative and appropriate for this format. '
  stmtText += `Contingency is held at ${budget.health.contingencyPct.toFixed(1)}% — ${budget.health.contingencyOk ? 'meeting standard bond company requirements.' : 'below industry minimum. Consider increasing to 10%.'}`
  return stmtText
}

export function buildIntelItems(budget, inputs) {
  const market = inputs.market || 'India'
  return [
    {
      cls: 'intel-item',
      title: 'ATL to BTL Ratio',
      text:
        budget.intel.atl2btl > 1
          ? `Every ${budget.intel.atl2btl} spent Above the Line, 1.00 goes Below the Line`
          : `1.00 ATL to ${(1 / budget.intel.atl2btl).toFixed(2)} BTL — production-heavy split`,
    },
    {
      cls: 'intel-item',
      title: 'Daily Production Cost',
      text: `Average ${budget.intel.perShootDay} per shoot day including all BTL costs at scale.`,
    },
    ...(inputs.nightShoots > 0
      ? [
          {
            cls: 'intel-amber intel-item',
            title: 'Night Shoot Premium',
            text: `${inputs.nightShoots} night shoots add approximately ${fmt(inputs.nightShoots * ((budget.btlTotal * 0.3) / inputs.shootDays) * 0.25, market)} in crew premium costs.`,
          },
        ]
      : []),
    ...(inputs.vfxShots > 30
      ? [
          {
            cls: 'intel-amber intel-item',
            title: 'VFX Budget Advisory',
            text: `${inputs.vfxShots} VFX shots is significant. Ensure post-production budget reflects vendor quotes, not estimates.`,
          },
        ]
      : []),
    budget.health.atlWarning
      ? {
          cls: 'intel-warn intel-item',
          title: 'ATL Overage Warning',
          text: 'ATL exceeds 50% of total budget. This will raise questions from bond companies. Consider trimming cast packages or deferring producer fees.',
        }
      : {
          cls: 'intel-ok intel-item',
          title: 'Budget Structure',
          text: 'Budget follows industry-standard ATL/BTL distribution. Suitable for presentation to financiers and bond companies.',
        },
    {
      cls: 'intel-item',
      title: 'Contingency Protocol',
      text: 'Do not spend contingency during pre-production or early production. Reserve for genuine overages — rain days, sick cast, equipment failure.',
    },
    ...(inputs.distrib === 'theatrical'
      ? [
          {
            cls: 'intel-item',
            title: 'Completion Bond',
            text: 'Theatrical release typically requires a completion bond. Budget 1-2% of total production cost for bond premium.',
          },
        ]
      : []),
    {
      cls: 'intel-item',
      title: 'Fringe Rates Advisory',
      text: 'Union productions add 20-35% fringe on top of labor rates (pension, health, payroll taxes). Ensure crew rates shown are gross, not base.',
    },
  ]
}
